// packages
import { intcomma } from 'journalize';
import { Popup } from 'mapbox-gl';

// components
import Typeahead from './Typeahead.svelte';

// local
import { createMap } from './create-map';
import { ACTUAL_LA_COUNTY_BOUNDS } from './constants';

function roundTo(value, roundBy = 1000) {
  return Math.round(value / roundBy) * roundBy;
}

function renderTooltip(props) {
  if (process.env.NODE_ENV === 'development') {
    console.log('rendered tooltip:', props);
  }

  return `<div class="tooltip">
    <p class="tooltip-title text-bold">${props.name}</p>
    ${
      props.insufficient_data
        ? '<div class="no-data">Insufficient data</div>'
        : `<dl class="tooltip-items">
      <div class="tooltip-item">
        <dt>Population</dt>
        <dd>${intcomma(props.population)}</dd>
      </div>
      <div class="tooltip-item">
        <dt>Median income</dt>
        <dd>$${intcomma(roundTo(props.median_income))}</dd>
      </div>
      <div class="tooltip-item">
        <dt>Nonwhite pop.</dt>
        <dd>${(props.non_white_percent * 100).toFixed(0)}%</dd>
      </div>
      <div class="tooltip-item">
        <dt>65+ pop.</dt>
        <dd>${(props.pop65up_percent * 100).toFixed(0)}%</dd>
      </div>
    </dl>
    <table class="tooltip-table">
      <thead>
        <tr>
          <th scope="col"></th>
          <th scope="col" class="text-right">Total</th>
          <th scope="col" class="text-right">Share of pop.</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row" class="text-left">Cases</th>
          <td class="text-right">${
            props.cases !== 'null' ? intcomma(props.cases) : '-'
          }</td>
          <td class="text-right">${
            props.cases_share !== 'null'
              ? `1 in ${intcomma(props.cases_share)}`
              : '-'
          }</td>
        </tr>
        <tr>
          <th scope="row" class="text-left">Deaths</th>
          <td class="text-right">${
            props.deaths !== 'null' ? intcomma(props.deaths) : '-'
          }</td>
          <td class="text-right">${
            props.deaths_share !== 'null'
              ? `1 in ${intcomma(props.deaths_share)}`
              : '-'
          }</td>
        </tr>
        <tr>
          <th scope="row" class="text-left">Vaccinations</th>
          <td class="text-right">${
            props.vaccinated !== 'null' ? intcomma(props.vaccinated) : '-'
          }</td>
          <td class="text-right">${
            props.vaccinated_share !== 'null'
              ? `1 in ${intcomma(props.vaccinated_share)}`
              : '-'
          }</td>
        </tr>
      </tbody>
    </table>`
    }
  </div>`;
}

export async function createInteractiveMap() {
  const items = JSON.parse(
    document.getElementById('places-lookup-data').textContent
  );
  const lookup = new Map(items.map((d) => [d.id, d]));

  const container = document.getElementById('lead-map');
  const legendContainer = document.getElementById('map-legend');
  const lookupContainer = document.getElementById('places-lookup');

  const map = await createMap({ container, bounds: ACTUAL_LA_COUNTY_BOUNDS });

  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8,
  });

  let activeId;

  function setActiveId(id, props, lngLat) {
    if (activeId) {
      map.setFeatureState({ source: 'places', id: activeId }, { hover: false });
    }

    map.setFeatureState({ source: 'places', id }, { hover: true });
    activeId = id;
    map.getCanvas().style.cursor = 'default';
    if (props && lngLat) {
      popup.setLngLat(lngLat).setHTML(renderTooltip(props)).addTo(map);
    }
  }

  function unsetActiveId() {
    if (activeId) {
      map.setFeatureState({ source: 'places', id: activeId }, { hover: false });

      activeId = undefined;
      map.getCanvas().style.cursor = '';
      popup.remove();
    }
  }

  map.on('mousemove', 'places-fill', (e) => {
    const features = e.features;

    if (features.length > 0) {
      const feature = features[0];
      const id = feature.id;
      const props = feature.properties;
      setActiveId(id, props, e.lngLat);
    }
  });

  map.on('mouseleave', 'places-fill', unsetActiveId);

  new Typeahead({
    target: lookupContainer,
    props: {
      label: 'Find a place',
      items,
      onSelect: (d) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('selected place:', d);
        }

        const place = lookup.get(d.id);

        if (place) {
          const lngLat = [place.x, place.y];
          const bounds = place.bounds;

          map.fitBounds(bounds, { padding: 40 });

          map.once('moveend', () => {
            const features = map.queryRenderedFeatures(map.project(lngLat), {
              layers: ['places-fill'],
            });

            if (features.length > 0) {
              const feature = features[0];
              const id = feature.id;
              const props = feature.properties;
              setActiveId(id, props, lngLat);
            }
          });
        }
      },
      onClear: unsetActiveId,
      getItemValue: (d) => d.name,
    },
  });
}
